// Create form for one staff user row — sales or catalog_manager
// (pages/UsersPage, admin console screen 4 of 5). Same "plain useState, no
// react-hook-form" shape as
// ProductForm.jsx — this app's create/edit forms don't use react-hook-form
// even though admin/CLAUDE.md's top-level stack note lists it as this repo's
// form library; LoginPage.jsx is the one screen that does, and this form
// follows its immediate sibling (ProductForm) instead, for consistency
// within this same "inline panel next to the trigger" family of forms.
//
// Role picker (migration 025, third staff role): POST /admin/users now
// accepts role: 'sales' | 'catalog_manager' (routes/adminUserRoutes.js's
// own createUserSchema — z.enum(['sales', 'catalog_manager'])). Still no
// admin-account option — 'admin' is deliberately not in that enum, matching
// prior behavior where there is no UI/API path to create an admin at all.
//
// Password uses shared/components/PasswordInput (root CLAUDE.md: "Build it
// once in shared/ and use it everywhere") — its first use outside a login
// form in admin/, wired as a plain controlled input (value/onChange) rather
// than react-hook-form's `register`, matching this form's own state shape.

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { PasswordInput } from 'shared/components/PasswordInput/index.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './UserForm.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_MIN_LENGTH = 8;

function initialState() {
  return { fullName: '', email: '', phone: '', password: '', role: 'sales' };
}

export default function UserForm({ onSubmit, onCancel, isPending }) {
  const { t, lang } = useLanguage();
  const [values, setValues] = useState(initialState);
  const [clientErrorId, setClientErrorId] = useState(null);

  const set = (field) => (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setClientErrorId(null);

    if (!values.fullName.trim()) {
      setClientErrorId('users.form.fullNameRequired');
      return;
    }
    if (!EMAIL_RE.test(values.email.trim())) {
      setClientErrorId('users.form.emailRequired');
      return;
    }
    // Matches routes/adminUserRoutes.js's createUserSchema: z.string().min(8)
    // — a plain minimum length, no composition rule (this is an admin
    // creating a colleague's account, not public signup).
    if (values.password.length < PASSWORD_MIN_LENGTH) {
      setClientErrorId('users.form.passwordTooShort');
      return;
    }

    onSubmit({
      fullName: values.fullName.trim(),
      email: values.email.trim(),
      phone: values.phone.trim() || undefined,
      password: values.password,
      role: values.role,
    });
  };

  const shownErrorId = clientErrorId;

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <h2 className={styles.title}>{t('users.form.createTitleGeneric')}</h2>

      <div className={styles.grid}>
        <label className={styles.field}>
          {t('users.form.roleLabel')}
          <select value={values.role} onChange={set('role')} className={styles.input}>
            <option value="sales">{t('users.role.sales')}</option>
            <option value="catalog_manager">{t('users.role.catalog_manager')}</option>
          </select>
        </label>

        <label className={styles.field}>
          {t('users.form.fullNameLabel')}
          <input
            type="text"
            value={values.fullName}
            onChange={set('fullName')}
            autoFocus
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('users.form.emailLabel')}
          <input
            type="email"
            value={values.email}
            onChange={set('email')}
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('users.form.phoneLabel')}
          <input type="tel" value={values.phone} onChange={set('phone')} className={styles.input} />
        </label>

        <label className={styles.field}>
          {t('users.form.passwordLabel')}
          <PasswordInput
            lang={lang}
            value={values.password}
            onChange={set('password')}
            autoComplete="new-password"
          />
          <span className={styles.hint}>{t('users.form.passwordHint')}</span>
        </label>
      </div>

      {shownErrorId && <p className={styles.error}>{t(shownErrorId)}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.cancelButton} onClick={onCancel}>
          {t('users.form.cancel')}
        </button>
        <button type="submit" className={styles.submitButton} disabled={isPending}>
          {isPending && <Spinner />}{' '}
          {isPending ? t('users.form.submitting') : t('users.form.submitCreate')}
        </button>
      </div>
    </form>
  );
}
